import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. Everything here is a line of light on
// absolute black. A deck wash would put a grey floor under the only thing
// worth looking at.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'polytope',
  family: 'vector',
  danceability: 0.70,
  energyLevel: 0.66,
  bpmMin: 95,
  bpmMax: 165,
  movement: 0.70,
  tonality: 0.55,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: true
};

// Polytope: four-dimensional regular polytopes turning in 4-space, projected
// to 3D and then to the screen, drawn as edges of light.
//
// A cube turning in 3D is understood at a glance. A hypercube turning in 4D
// CANNOT be: its inner cells pass to the outside, it turns itself inside out,
// and yet not one edge changes length. Nothing here deforms - the object is
// perfectly rigid, and it is our space that is too small to hold it. That is
// the whole subject, and every decision below serves it:
//
//   - the colour of an edge is its FOURTH coordinate. w = -1 takes one hue of
//     the palette, w = +1 its complementary, and the gradient runs along the
//     edge. As the object turns in a plane containing w, every edge slides
//     continuously through the hue range. This is the one thing on screen
//     that shows the dimension we cannot see, and it is why the hue is not a
//     decoration here.
//   - the 4D eye distance uEye4 is the strength of the illusion. Near 1 the
//     inner cells are tiny and the outer ones fill the frame; large, the
//     projection flattens to something almost orthogonal and the turning
//     stops reading. 2.4 at rest.
//   - the rotation advances in NOTCHES on the beats rather than continuously,
//     so the object snaps from position to position and the eye gets a fresh
//     configuration to parse on every beat instead of a smooth blur.
//
// MEASURED, NOT ASSUMED: the five vertex/edge sets are generated from their
// standard coordinates and then CHECKED against their known counts at init
// (16/32, 8/24, 24/96, 600/1200, 120/720) plus the requirement that every
// vertex sit at the same distance from the origin. A polytope that fails its
// own check is dropped from the cycle rather than drawn wrong - see
// _construireFormes. ?polycheck=1 prints the table.

const PHI = (1 + Math.sqrt(5)) / 2;

// L'OEIL 4D. 2.4 au repos ; sur un drop il tombe à 1.15 et la projection
// explose. Il ne peut PAS descendre à 1.0 : les sommets sont sur l'hypersphère
// unité, donc w atteint 1, et l'échelle uEye4/(uEye4 - w) passerait par
// l'infini. 1.15 laisse 0.15 de marge, ce qui donne déjà un facteur 7.7 entre
// la cellule la plus proche et la plus lointaine.
const OEIL4_BASE = 2.4;
const OEIL4_DROP = 1.15;
const OEIL4_MIN = 1.12;
const DROP_MESURES = 2;          // remontée de l'oeil après le drop
const OEIL4_BANDE = 0.15;        // bands.low, +-15%

// LA ROTATION. Une phrase vaut seize temps, et le plan principal avance d'un
// seizième de tour par temps : une révolution complète par phrase, exactement
// comme demandé, sans que les deux réglages puissent diverger.
const TEMPS_PAR_PHRASE = 16;
const CRAN_MS = 120;             // l'interpolation d'un cran à l'autre
const PLANS = { xy: [0, 1], xz: [0, 2], xw: [0, 3], yz: [1, 2], yw: [1, 3], zw: [2, 3] };
// Sur chaque mesure le plan principal change. Trois plans contenant w : le
// retournement change d'axe et l'oeil ne trouve pas de motif.
const CYCLE_PLANS = ['xw', 'yw', 'zw'];

// LE RAYON VISÉ, en unités de scène, et non un facteur d'échelle global : les
// cinq polytopes n'ont pas du tout la même étendue une fois projetés - le
// tesseract met ses seize sommets aux coins, le 600-cell en répartit cent
// vingt sur une boule. Un facteur commun en cadrait un correctement et
// laissait les autres minuscules. L'échelle est donc calculée PAR FORME, au
// repos, pour que chacune remplisse le cadre de la même façon.
const RAYON_VISE = 2.95;
const CAMERA_DIST = 7.2;
const CAMERA_TOUR_S = 90;

// LE TRAIT. Le quad est large de uDemiQuad de part et d'autre de l'axe : le
// halo l'occupe entièrement et le coeur presque blanc n'en prend que quelques
// pixels, dont la largeur suit la profondeur 4D. La demi-largeur est un
// uniforme et non une constante parce qu'elle dépend du tier : le coût sur le
// Pi est de la SURFACE, environ 7 microsecondes par arête, et un quad plus
// étroit est la seule économie qui ne retire rien de la géométrie.

const COEUR_PX = [0.55, 1.40];    // demi-largeur : 1.5 px au loin, 4 px au près
const HALO_NIVEAU = 0.62;
const COEUR_NIVEAU = 0.94;       // luminance de crête : les arêtes proches bloomen
// « 4% » et « 6% » sont des pourcentages de SORTIE. Posés tels quels dans un
// espace linéaire, avant ACES et la courbe sRGB dont la pente près du noir
// vaut environ 13, ils ressortent trois à quatre fois trop clairs : la
// première capture montrait une grille de fond aussi lumineuse que le sujet.
// Les deux valeurs ci-dessous sont étalonnées sur l'octet mesuré.
const GRILLE_NIVEAU = 0.0045;   // mesuré : 18/255 à 0.006, 7/255 à 0.0032 ; visé 10/255 (4%)
// Les faces des cellules. « 6% » est un pourcentage de SORTIE, pas une valeur
// linéaire : la courbe sRGB a une pente proche de 13 en bas, donc 0.06 posé
// ici ressortirait vers 74/255. Étalonné à la mesure, voir plus bas.
const CELLULE_NIVEAU = 0.010;   // mesuré : 14/255, soit les 6% demandés
const SOMMET_PX = 4.6;
const SOMMET_NIVEAU = 0.60;

// L'atténuation par la profondeur 3D, la seconde des deux profondeurs.
const PROF3D = [0.42, 1.0];

const PLANCHER_PARTAGE = 0.32;
const PLANCHER = 0.45;
const LUMA = 'vec3(0.2125, 0.7154, 0.0721)';

// LE GAIN PAR FORME. De trente-deux à mille deux cents arêtes, en additif et
// sous bloom, la même luminosité donne un fil d'un côté et un ballon blanc de
// l'autre : mesuré à gain égal, la luminance moyenne du cadre va de 4.8 pour
// le 16-cell à 84.7 pour le 600-cell, un facteur dix-sept. Les valeurs
// ci-dessous sont relevées, pas déduites d'une formule sur le nombre d'arêtes
// - ce nombre ne dit rien de la surface réellement couverte, et le 600-cell,
// qui a moins d'arêtes que le 120-cell, couvre davantage parce que les
// siennes sont courtes et ramassées. Plafonné à 2 : au-delà, une forme creuse
// ne gagne plus en crête, seulement en halo.
const GAIN_FORME = { tesseract: 1.70, '16cell': 1.70, '24cell': 0.78, '600cell': 0.153, '120cell': 0.238 };

const MORPH_S = 3.0;
const CYCLE_FORMES = ['tesseract', '24cell', '120cell', '16cell', '600cell'];

// LES TIERS, fixés à la MESURE sur le Pi (1280x400, visualiseur seul, un rendu
// suivi d'un readPixels) : 120-cell 10,5 ms, 600-cell 11,2, tesseract 4,0.
// Le compositeur partagé ajoute environ 11 ms, donc les deux formes denses
// dépassent le budget de 16,7 ms sur le Pi et n'appartiennent qu'au tier 2.
// Le coût est de la SURFACE, pas du sommet : chaque arête est un quad de douze
// pixels de large en travers de l'écran, soit environ 7 microsecondes par
// arête. D'où aussi la largeur du trait par tier.
const TIERS = {
  2: { formes: ['tesseract', '16cell', '24cell', '120cell', '600cell'], cellules: true, grille: true, demiQuad: 6.0 },
  1: { formes: ['tesseract', '16cell', '24cell'], cellules: true, grille: true, demiQuad: 5.0 },
  0: { formes: ['tesseract', '16cell', '24cell'], cellules: false, grille: false, demiQuad: 4.0 }
};

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
function urlParam(nom) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(nom);
}

// ---------------------------------------------------------------------------
// LES POLYTOPES. Coordonnées standard, jamais improvisées, et vérifiées par
// leur compte de sommets et d'arêtes.
// ---------------------------------------------------------------------------

const PERMS = [];
(function gen(reste, acc) {
  if (!reste.length) { PERMS.push(acc); return; }
  for (let i = 0; i < reste.length; i++)
    gen(reste.slice(0, i).concat(reste.slice(i + 1)), acc.concat([reste[i]]));
})([0, 1, 2, 3], []);
const PAIRES = PERMS.filter((p) => {
  let n = 0;
  for (let i = 0; i < 4; i++) for (let j = i + 1; j < 4; j++) if (p[i] > p[j]) n++;
  return n % 2 === 0;
});

const cle4 = (v) => v.map((x) => (Math.abs(x) < 1e-9 ? 0 : x).toFixed(6)).join(',');

// Toutes les permutations (ou les paires) d'un gabarit, tous les signes de ses
// coordonnées non nulles. Le DÉDOUBLONNAGE n'est pas une précaution : un
// gabarit qui porte deux coordonnées égales ou une coordonnée nulle produit
// des répétitions, et c'est exactement là qu'un compte faux se cache.
function deployer(gabarit, permutations) {
  const vus = new Map();
  const nz = [];
  for (let i = 0; i < 4; i++) if (Math.abs(gabarit[i]) > 1e-9) nz.push(i);
  for (let m = 0; m < (1 << nz.length); m++) {
    const g = gabarit.slice();
    for (let k = 0; k < nz.length; k++) if (m & (1 << k)) g[nz[k]] = -g[nz[k]];
    for (const p of permutations) {
      const v = [g[p[0]], g[p[1]], g[p[2]], g[p[3]]];
      const c = cle4(v);
      if (!vus.has(c)) vus.set(c, v);
    }
  }
  return [...vus.values()];
}
function fusionner(listes) {
  const vus = new Map();
  for (const l of listes) for (const v of l) { const c = cle4(v); if (!vus.has(c)) vus.set(c, v); }
  return [...vus.values()];
}

function sommetsTesseract() {
  const s = [];
  for (let m = 0; m < 16; m++)
    s.push([m & 1 ? 1 : -1, m & 2 ? 1 : -1, m & 4 ? 1 : -1, m & 8 ? 1 : -1]);
  return s;
}
const sommets16 = () => deployer([1, 0, 0, 0], PERMS);
const sommets24 = () => deployer([1, 1, 0, 0], PERMS);
function sommets600() {
  const a = [];
  for (let m = 0; m < 16; m++)
    a.push([m & 1 ? .5 : -.5, m & 2 ? .5 : -.5, m & 4 ? .5 : -.5, m & 8 ? .5 : -.5]);
  return fusionner([a, deployer([1, 0, 0, 0], PERMS),
                    deployer([PHI / 2, .5, 1 / (2 * PHI), 0], PAIRES)]);
}
// LE 120-CELL EST CONSTRUIT COMME LE DUAL DU 600-CELL, et pas depuis sa propre
// liste de coordonnées. Les deux listes standard existent, chacune est juste,
// mais elles ne sont pas en POSITION DUALE l'une de l'autre : mesuré, 48
// directions sur 75 prises dans l'une pointaient vers un sommet de l'autre au
// lieu d'un centre de cellule, et les cellules ne se trouvaient pas. Les
// centres des six cents cellules tétraédriques du 600-cell - ses 4-cliques
// d'arêtes, exactement six cents, vérifiées - donnent un 120-cell dont la
// position duale est vraie par construction. On reste bien « depuis les
// coordonnées du nombre d'or » : ce sont celles du 600-cell.
let _cache120 = null;
function sommets120() {
  if (_cache120) return _cache120;
  const S = normaliser(sommets600());
  const A = aretesDe(S);
  const voisin = S.map(() => new Set());
  for (let e = 0; e < A.length; e += 2) { voisin[A[e]].add(A[e + 1]); voisin[A[e + 1]].add(A[e]); }
  const out = [];
  for (let a = 0; a < S.length; a++) for (const b of voisin[a]) { if (b < a) continue;
    for (const c of voisin[a]) { if (c < b || !voisin[b].has(c)) continue;
      for (const d of voisin[a]) { if (d < c || !voisin[b].has(d) || !voisin[c].has(d)) continue;
        out.push([0, 1, 2, 3].map((k) => (S[a][k] + S[b][k] + S[c][k] + S[d][k]) / 4));
      } } }
  _cache120 = out;
  return out;
}

// LES ARÊTES SE DÉDUISENT, elles ne se listent pas. Dans un polytope régulier
// la plus petite distance entre deux sommets EST la longueur d'arête, et deux
// sommets à cette distance sont voisins. Aucune table à recopier, donc aucune
// table à recopier de travers.
function aretesDe(sommets) {
  let min = Infinity;
  for (let i = 0; i < sommets.length; i++)
    for (let j = i + 1; j < sommets.length; j++) {
      let d = 0;
      for (let k = 0; k < 4; k++) { const e = sommets[i][k] - sommets[j][k]; d += e * e; }
      if (d < min) min = d;
    }
  const seuil = min * 1.0001, out = [];
  for (let i = 0; i < sommets.length; i++)
    for (let j = i + 1; j < sommets.length; j++) {
      let d = 0;
      for (let k = 0; k < 4; k++) { const e = sommets[i][k] - sommets[j][k]; d += e * e; }
      if (d <= seuil) out.push(i, j);
    }
  return out;
}

function normaliser(sommets) {
  return sommets.map((v) => {
    const n = Math.hypot(v[0], v[1], v[2], v[3]);
    return [v[0] / n, v[1] / n, v[2] / n, v[3] / n];
  });
}

// LES CELLULES, par le DUAL. Les centres des cellules d'un polytope régulier
// sont, en direction, les sommets de son dual : tesseract et 16-cell sont
// duaux l'un de l'autre, le 24-cell est son propre dual, 120-cell et 600-cell
// se répondent. Une cellule est alors l'ensemble des sommets qui maximisent le
// produit scalaire avec la direction de son centre - rien à tabuler, et le
// compte de sommets par cellule se vérifie (cube 8, tétraèdre 4, octaèdre 6,
// dodécaèdre 20).
// ATTENTION au 24-cell : il est auto-dual, mais son dual est un 24-cell dans
// une AUTRE ORIENTATION, pas le même jeu de sommets. Prendre ses propres
// sommets comme directions de cellules ne trouve aucune cellule - mesuré : zéro
// cellule et zéro triangle, en silence. Les centres de ses vingt-quatre
// octaèdres pointent vers les huit sommets du 16-cell et les seize du
// tesseract, ce qui fait bien vingt-quatre directions.
const DIRECTIONS_CELLULES = {
  tesseract: () => sommets16(),
  '16cell': () => sommetsTesseract(),
  '24cell': () => fusionner([sommets16(), sommetsTesseract()]),
  '120cell': () => sommets600(),
  '600cell': () => sommets120()   // dual par construction, voir sommets120()
};

function cellulesDe(sommets, directions, uneSur) {
  const out = [];
  for (let c = 0; c < directions.length; c += uneSur) {
    const d = directions[c];
    let max = -Infinity;
    for (const v of sommets) {
      const p = v[0] * d[0] + v[1] * d[1] + v[2] * d[2] + v[3] * d[3];
      if (p > max) max = p;
    }
    const idx = [];
    for (let i = 0; i < sommets.length; i++) {
      const v = sommets[i];
      const p = v[0] * d[0] + v[1] * d[1] + v[2] * d[2] + v[3] * d[3];
      if (p > max - 1e-6) idx.push(i);
    }
    if (idx.length >= 4) out.push({ dir: d, idx: idx });
  }
  return out;
}

// Triangule l'enveloppe convexe d'une cellule. Elle vit dans l'hyperplan
// orthogonal à la direction de son centre : on l'y exprime dans une base
// orthonormée à trois vecteurs, et on y fait une enveloppe convexe 3D. Les
// triangles obtenus sont des INDICES, donc valables à chaque image quelle que
// soit la rotation 4D - le calcul n'a lieu qu'une fois.
function baseHyperplan(d) {
  // Trois vecteurs orthonormés orthogonaux à d, par Gram-Schmidt sur les axes.
  const base = [];
  const axes = [[1, 0, 0, 0], [0, 1, 0, 0], [0, 0, 1, 0], [0, 0, 0, 1]];
  for (const a of axes) {
    if (base.length === 3) break;
    let v = a.slice();
    const pd = v[0] * d[0] + v[1] * d[1] + v[2] * d[2] + v[3] * d[3];
    for (let k = 0; k < 4; k++) v[k] -= pd * d[k];
    for (const b of base) {
      const pb = v[0] * b[0] + v[1] * b[1] + v[2] * b[2] + v[3] * b[3];
      for (let k = 0; k < 4; k++) v[k] -= pb * b[k];
    }
    const n = Math.hypot(v[0], v[1], v[2], v[3]);
    if (n > 1e-6) base.push(v.map((x) => x / n));
  }
  return base;
}

function trianglesCellule(sommets, cellule) {
  const B = baseHyperplan(cellule.dir);
  if (B.length < 3) return [];
  const P = cellule.idx.map((i) => {
    const v = sommets[i];
    return [0, 1, 2].map((k) => v[0] * B[k][0] + v[1] * B[k][1] + v[2] * B[k][2] + v[3] * B[k][3]);
  });
  const n = P.length;
  // Les faces sont les plans d'APPUI : un triple en définit un si tous les
  // autres points sont du même côté. Une face à plus de trois sommets - le
  // carré d'un cube, le pentagone d'un dodécaèdre - est trouvée par plusieurs
  // triples, qu'il faut réunir en UNE face.
  //
  // La clef est l'ENSEMBLE DES POINTS du plan, jamais sa normale. Clefée sur
  // la normale arrondie, une même face pentagonale ressortait sous plusieurs
  // clefs - deux triples de la même face donnent des normales qui diffèrent au
  // septième chiffre - et se retrouvait triangulée deux fois : 46 triangles
  // par dodécaèdre au lieu de 36, et une surface qui n'était plus fermée.
  const faces = new Map();
  for (let a = 0; a < n; a++) for (let b = a + 1; b < n; b++) for (let c = b + 1; c < n; c++) {
    const u = [P[b][0] - P[a][0], P[b][1] - P[a][1], P[b][2] - P[a][2]];
    const w = [P[c][0] - P[a][0], P[c][1] - P[a][1], P[c][2] - P[a][2]];
    let nx = u[1] * w[2] - u[2] * w[1], ny = u[2] * w[0] - u[0] * w[2], nz = u[0] * w[1] - u[1] * w[0];
    const ln = Math.hypot(nx, ny, nz);
    if (ln < 1e-7) continue;                        // trois points alignés
    nx /= ln; ny /= ln; nz /= ln;
    const off = nx * P[a][0] + ny * P[a][1] + nz * P[a][2];
    let dessus = 0, dessous = 0;
    for (let k = 0; k < n; k++) {
      const d = nx * P[k][0] + ny * P[k][1] + nz * P[k][2] - off;
      if (d > 1e-6) dessus++; else if (d < -1e-6) dessous++;
    }
    if (dessus && dessous) continue;                // ce n'est pas un plan d'appui
    const sg = dessus ? -1 : 1;                     // la normale regarde dehors
    const N = [sg * nx, sg * ny, sg * nz], O = sg * off;
    const sur = [];
    for (let k = 0; k < n; k++)
      if (Math.abs(N[0] * P[k][0] + N[1] * P[k][1] + N[2] * P[k][2] - O) < 1e-6) sur.push(k);
    if (sur.length < 3) continue;
    const clef = sur.join('-');
    if (!faces.has(clef)) faces.set(clef, { n: N, sur: sur });
  }
  const tris = [];
  for (const [, face] of faces) {
    const sur = face.sur.slice(), N = face.n;
    // Ordonner autour du centre de la face : un éventail sur des points non
    // ordonnés se croise et la face est trouée.
    const ctr = [0, 0, 0];
    for (const k of sur) for (let j = 0; j < 3; j++) ctr[j] += P[k][j] / sur.length;
    let e1 = [P[sur[0]][0] - ctr[0], P[sur[0]][1] - ctr[1], P[sur[0]][2] - ctr[2]];
    const l1 = Math.hypot(e1[0], e1[1], e1[2]);
    if (l1 < 1e-9) continue;
    e1 = e1.map((x) => x / l1);
    const e2 = [N[1] * e1[2] - N[2] * e1[1], N[2] * e1[0] - N[0] * e1[2], N[0] * e1[1] - N[1] * e1[0]];
    const ang = (k) => {
      const v = [P[k][0] - ctr[0], P[k][1] - ctr[1], P[k][2] - ctr[2]];
      return Math.atan2(v[0] * e2[0] + v[1] * e2[1] + v[2] * e2[2],
                        v[0] * e1[0] + v[1] * e1[1] + v[2] * e1[2]);
    };
    sur.sort((a, b) => ang(a) - ang(b));
    for (let k = 1; k + 1 < sur.length; k++)
      tris.push(cellule.idx[sur[0]], cellule.idx[sur[k]], cellule.idx[sur[k + 1]]);
  }
  return tris;
}

// Le compte exact de chaque polytope. Ce n'est pas de la documentation : une
// forme qui rate son compte est RETIRÉE du cycle plutôt que dessinée fausse.
export const ATTENDUS = {
  tesseract: { sommets: 16, aretes: 32, parCellule: 8 },
  '16cell': { sommets: 8, aretes: 24, parCellule: 4 },
  '24cell': { sommets: 24, aretes: 96, parCellule: 6 },
  '120cell': { sommets: 600, aretes: 1200, parCellule: 20 },
  '600cell': { sommets: 120, aretes: 720, parCellule: 4 }
};
const GENERATEURS = {
  tesseract: sommetsTesseract, '16cell': sommets16, '24cell': sommets24,
  '120cell': sommets120, '600cell': sommets600
};
// Une cellule sur huit, comme demandé.
const UNE_CELLULE_SUR = 8;

// Construit une forme et la VÉRIFIE. Exporté parce que le test en a besoin :
// le critère 1 se contrôle sans navigateur ni GPU.
export function construireForme(nom, avecCellules) {
  const brut = GENERATEURS[nom]();
  const sommets = normaliser(brut);
  const aretes = aretesDe(sommets);
  const att = ATTENDUS[nom];
  const rayons = sommets.map((v) => Math.hypot(v[0], v[1], v[2], v[3]));
  const ecartRayon = Math.max(...rayons) - Math.min(...rayons);
  const bon = sommets.length === att.sommets && aretes.length / 2 === att.aretes && ecartRayon < 1e-9;
  let tris = [], parCellule = 0;
  const parCell = [];
  if (bon && avecCellules) {
    const dirs = normaliser(DIRECTIONS_CELLULES[nom]());
    const cells = cellulesDe(sommets, dirs, UNE_CELLULE_SUR);
    parCellule = cells.length ? cells[0].idx.length : 0;
    // Une cellule au mauvais nombre de sommets veut dire que le dual n'est pas
    // celui qu'on croit : on préfère aucune cellule à des cellules fausses.
    if (parCellule === att.parCellule)
      for (const c of cells) {
        const t = trianglesCellule(sommets, c);
        parCell.push(t);
        tris = tris.concat(t);
      }
    else tris = [];
  }
  // L'échelle au repos : le rayon visé divisé par la plus grande distance au
  // centre que la projection produit avec l'oeil au repos.
  let etendue = 0;
  for (const v of sommets) {
    const k = OEIL4_BASE / (OEIL4_BASE - v[3]);
    etendue = Math.max(etendue, Math.hypot(v[0], v[1], v[2]) * k);
  }
  return {
    nom: nom, ok: bon, sommets: sommets, aretes: aretes, tris: tris,
    echelle: etendue > 0 ? RAYON_VISE / etendue : 1,
    // Les triangles AUSSI cellule par cellule : la fermeture d'un polyèdre se
    // vérifie sur lui seul. Agrégées, deux cellules voisines partagent des
    // indices de sommets et chaque arête commune est comptée quatre fois.
    trisParCellule: parCell,
    parCellule: parCellule, ecartRayon: ecartRayon
  };
}

// ---------------------------------------------------------------------------
// LES SHADERS
// ---------------------------------------------------------------------------

const ZONE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  float zoneSecurite(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float a = mix(${PLANCHER_PARTAGE.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${PLANCHER.toFixed(2)} + (a - ${PLANCHER_PARTAGE.toFixed(2)})
         * ${((1 - PLANCHER) / (1 - PLANCHER_PARTAGE)).toFixed(6)};
  }
`;

// LE PLAFOND est posé sur la LUMINANCE et non sur le canal le plus fort. Le
// seuil du bloom est une luminance : borner le canal max laisse passer des
// couleurs dont la luminance est trois fois plus basse, et le coeur presque
// blanc d'une arête, écrêté ainsi, cesse de bloomer sans qu'on voie pourquoi.
const PLAFOND_CHUNK = `
  vec3 plafond(vec3 c, float crete) {
    float l = dot(c, ${LUMA});
    return l > crete ? c * (crete / max(l, 1e-4)) : c;
  }
`;

const ARETE_VERT = `
  ${ZONE_CHUNK}
  attribute vec3 iA;
  attribute vec3 iB;
  attribute vec2 iW;          // w du sommet A, w du sommet B
  uniform float uHauteurCss;  // hauteur du canvas en pixels CSS
  // LE W RÉELLEMENT ATTEINT, et non 1. Les sommets sont sur l'hypersphère
  // unité, mais un tesseract au repos n'a que w = +-0.5 et un 16-cell +-1 :
  // normaliser sur 1 n'utilisait que la moitié de la plage de teintes et
  // toutes les arêtes sortaient dans les mêmes tons moyens. La quatrième
  // dimension doit se lire, donc l'échelle de teinte suit ce que w fait
  // vraiment - recalculé à chaque image, puisque la rotation le change.
  uniform float uWmax;
  uniform float uDemiQuad;   // demi-largeur du trait, en pixels CSS
  varying float vCote;
  varying float vW;
  varying float vCoeurPx;
  varying float vAtt;
  varying float vZone;

  void main() {
    float t = position.x;       // 0 au sommet A, 1 au sommet B
    float cote = position.y;    // -1 / +1 en travers du trait
    vec4 mva = modelViewMatrix * vec4(iA, 1.0);
    vec4 mvb = modelViewMatrix * vec4(iB, 1.0);
    vec4 ca = projectionMatrix * mva;
    vec4 cb = projectionMatrix * mvb;
    vec2 na = ca.xy / ca.w;
    vec2 nb = cb.xy / cb.w;
    // La direction se mesure en pixels, pas en NDC : sans la correction
    // d'aspect, un trait vertical et un trait horizontal n'ont pas la même
    // épaisseur à l'écran.
    vec2 d = (nb - na) * vec2(uAspect, 1.0);
    float ld = length(d);
    vec2 dir = ld > 1e-5 ? d / ld : vec2(1.0, 0.0);
    vec2 nor = vec2(-dir.y, dir.x) / vec2(uAspect, 1.0);
    vec4 c = mix(ca, cb, t);
    // Le décalage est multiplié par c.w pour SURVIVRE à la division
    // perspective qui suit : sans ça un trait lointain serait plus fin.
    c.xy += nor * cote * (2.0 * uDemiQuad / uHauteurCss) * c.w;
    gl_Position = c;

    vCote = cote;
    vW = mix(iW.x, iW.y, t) / uWmax;
    // w = +1 est le côté de l'oeil 4D : c'est la PREMIÈRE des deux
    // profondeurs, et elle règle l'épaisseur du coeur.
    float prof4 = clamp(vW * 0.5 + 0.5, 0.0, 1.0);
    vCoeurPx = mix(${COEUR_PX[0].toFixed(2)}, ${COEUR_PX[1].toFixed(2)}, prof4);
    // La SECONDE profondeur, celle de la caméra 3D, atténue.
    float dist = -mix(mva.z, mvb.z, t);
    float k = clamp((dist - ${(CAMERA_DIST - 3.2).toFixed(2)}) / 6.4, 0.0, 1.0);
    vAtt = mix(${PROF3D[1].toFixed(2)}, ${PROF3D[0].toFixed(2)}, k);
    vZone = zoneSecurite(mix(na, nb, t));
  }
`;

const ARETE_FRAG = `
  ${PLAFOND_CHUNK}
  uniform vec3 uMoins;
  uniform vec3 uPlus;
  uniform float uNiveau;
  uniform float uDither;
  uniform float uDemiQuad;
  varying float vCote;
  varying float vW;
  varying float vCoeurPx;
  varying float vAtt;
  varying float vZone;

  void main() {
    float a = abs(vCote);
    float px = a * uDemiQuad;
    // Le halo occupe tout le quad, le coeur quelques pixels au centre.
    float halo = pow(max(0.0, 1.0 - a), 2.2);
    float coeur = 1.0 - smoothstep(vCoeurPx - 0.6, vCoeurPx + 0.6, px);
    // LA QUATRIÈME DIMENSION EST LA TEINTE. w = -1 prend la teinte de la
    // pochette, w = +1 sa complémentaire, et le dégradé court le long de
    // l'arête : quand l'objet tourne dans un plan contenant w, chaque arête
    // glisse continûment d'une teinte à l'autre.
    vec3 teinte = mix(uMoins, uPlus, clamp(vW * 0.5 + 0.5, 0.0, 1.0));
    vec3 col = teinte * halo * ${HALO_NIVEAU.toFixed(2)}
             + mix(teinte, vec3(1.0), 0.30) * coeur * ${COEUR_NIVEAU.toFixed(2)};
    col *= vAtt * vZone * uNiveau;
    // Un tramage ORDONNÉ, multiplicatif : à 4% sur du noir la grille de fond
    // tient dans deux ou trois valeurs et se range en bandes. Ordonné et non
    // haché parce qu'un sinus par pixel, sur une surface, se paie - la ligne
    // d'arbres de fireflies l'a montré.
    float b = fract(dot(floor(gl_FragCoord.xy), vec2(0.0625, 0.125)));
    col *= 1.0 - uDither * (0.5 - b);
    gl_FragColor = vec4(plafond(col, ${COEUR_NIVEAU.toFixed(2)}), 1.0);
  }
`;

const SOMMET_VERT = `
  ${ZONE_CHUNK}
  attribute float aW;
  uniform float uWmax;
  uniform float uDpr;
  uniform float uScint;
  varying float vW;
  varying float vAtt;
  varying float vZone;

  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vec4 c = projectionMatrix * mv;
    gl_Position = c;
    vW = aW / uWmax;
    float prof4 = clamp(vW * 0.5 + 0.5, 0.0, 1.0);
    gl_PointSize = ${SOMMET_PX.toFixed(2)} * uDpr * mix(0.55, 1.45, prof4) * (0.80 + 0.20 * uScint);
    float k = clamp((-mv.z - ${(CAMERA_DIST - 3.2).toFixed(2)}) / 6.4, 0.0, 1.0);
    vAtt = mix(${PROF3D[1].toFixed(2)}, ${PROF3D[0].toFixed(2)}, k);
    vZone = zoneSecurite(c.xy / c.w);
  }
`;

const SOMMET_FRAG = `
  ${PLAFOND_CHUNK}
  uniform vec3 uMoins;
  uniform vec3 uPlus;
  uniform float uNiveau;
  uniform float uScint;
  varying float vW;
  varying float vAtt;
  varying float vZone;

  void main() {
    float d = length(gl_PointCoord - 0.5) * 2.0;
    if (d > 1.0) discard;
    float n = pow(max(0.0, 1.0 - d), 2.0);
    vec3 teinte = mix(uMoins, uPlus, clamp(vW * 0.5 + 0.5, 0.0, 1.0));
    vec3 col = mix(teinte, vec3(1.0), 0.55) * n * uNiveau * (0.75 + 0.45 * uScint) * vAtt * vZone;
    gl_FragColor = vec4(plafond(col, ${COEUR_NIVEAU.toFixed(2)}), 1.0);
  }
`;

// LES CELLULES. Des faces translucides, une cellule sur huit : on voit alors
// des volumes fantômes qui se retournent, et pas seulement un fil de fer.
const CELLULE_VERT = `
  ${ZONE_CHUNK}
  attribute float aW;
  uniform float uWmax;
  varying float vW;
  varying float vZone;
  varying float vAtt;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vec4 c = projectionMatrix * mv;
    gl_Position = c;
    vW = aW / uWmax;
    float k = clamp((-mv.z - ${(CAMERA_DIST - 3.2).toFixed(2)}) / 6.4, 0.0, 1.0);
    vAtt = mix(1.0, 0.35, k);
    vZone = zoneSecurite(c.xy / c.w);
  }
`;
const CELLULE_FRAG = `
  uniform vec3 uMoins;
  uniform vec3 uPlus;
  uniform float uNiveau;
  varying float vW;
  varying float vZone;
  varying float vAtt;
  void main() {
    vec3 teinte = mix(uMoins, uPlus, clamp(vW * 0.5 + 0.5, 0.0, 1.0));
    gl_FragColor = vec4(teinte * uNiveau * vAtt * vZone, 1.0);
  }
`;

// ---------------------------------------------------------------------------

const _taille = new THREE.Vector2();
const RICHESSE = { tesseract: 0, '16cell': 0, '24cell': 1, '600cell': 2, '120cell': 3 };

export class PolytopeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._plan = TIERS[this._tier] || TIERS[1];
    this._formeForcee = urlParam('polyshape');
    this._planForce = urlParam('polyplane');
    const e = parseFloat(urlParam('polyeye') || '');
    this._oeilForce = Number.isFinite(e) ? Math.max(OEIL4_MIN, e) : null;
    this._pasLibre = urlParam('polystep') === '1';
    this._verifie = urlParam('polycheck') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._temps = 0;

    this._construireFormes();
    const noms = Object.keys(this._formes);
    if (!noms.length) throw new Error('polytope: aucune forme valide');

    let maxS = 0, maxA = 0, maxT = 0;
    for (const n of noms) {
      const f = this._formes[n];
      maxS = Math.max(maxS, f.sommets.length);
      maxA = Math.max(maxA, f.aretes.length / 2);
      maxT = Math.max(maxT, f.tris.length);
    }
    this._maxS = maxS;

    // Deux jeux de tampons : pendant un morphing on dessine la forme de
    // DÉPART, dont les sommets volent, et on fait entrer la forme d'ARRIVÉE en
    // fondu croisé sur la dernière seconde. Les niveaux somment à 1, sinon les
    // deux se superposent en additif et l'objet double de clarté à l'atterrissage.
    this._rot = new Float32Array(maxS * 4);
    this._rotB = new Float32Array(maxS * 4);
    this._pos3 = new Float32Array(maxS * 3);
    this._pos3B = new Float32Array(maxS * 3);
    this._w = new Float32Array(maxS);
    this._wB = new Float32Array(maxS);

    this._uni = {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather,
      uMoins: { value: new THREE.Color(0.20, 0.55, 1.0) },
      uPlus: { value: new THREE.Color(1.0, 0.45, 0.18) },
      uHauteurCss: { value: 720 },
      uDpr: { value: 1 },
      uScint: { value: 0 },
      uDemiQuad: { value: this._plan.demiQuad }
    };

    this._aretesA = this._creerAretes(maxA, 1);
    this._aretesB = this._creerAretes(maxA, 0);
    this._sommetsA = this._creerSommets(maxS, 1);
    this._sommetsB = this._creerSommets(maxS, 0);
    this._cellules = this._plan.cellules && maxT ? this._creerCellules(maxT) : null;
    this._grille = this._plan.grille ? this._creerGrille() : null;

    const self = this;
    this._aretesA.onBeforeRender = function (renderer) {
      renderer.getSize(_taille);
      if (_taille.y > 0) self._uni.uHauteurCss.value = _taille.y;
    };

    // L'état de la séquence et des rotations.
    this._nomCourant = this._formeForcee && this._formes[this._formeForcee]
      ? this._formeForcee : this._premiereForme();
    this._nomSuivant = null;
    this._morph = -1;
    this._assoc = null;
    this._idxCycle = Math.max(0, CYCLE_FORMES.indexOf(this._nomCourant));

    // DEUX jeux d'angles : la CIBLE, qui saute d'un seizième de tour sur
    // chaque temps, et le VU, qui la rattrape en 120 ms. C'est cet écart qui
    // fait claquer le polytope de position en position au lieu de glisser.
    this._cible = { xy: 0, xz: 0, xw: 0, yz: 0, yw: 0, zw: 0 };
    this._vu = { xy: 0, xz: 0, xw: 0, yz: 0, yw: 0, zw: 0 };
    this._pulseSommets = 0;
    this._planPrincipal = this._planForce && PLANS[this._planForce] ? this._planForce : CYCLE_PLANS[0];
    this._cran = 0; this._cranPrec = 0; this._cranT = 1;
    this._angleSec = 0; this._angleTer = 0;
    this._battements = 0; this._mesures = 0; this._phrases = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._secondesBeat = 0.5;
    this._oeil = OEIL4_BASE; this._oeilCible = OEIL4_BASE;
    this._dropJusqu = -1; this._dropTenue = -1; this._modePrec = null;
    this._paletteVue = undefined;
    this._pasLibreAt = 0;
    this._appliquerPalette(null);
    this._camera.position.set(0, 1.4, CAMERA_DIST);
    this._camera.lookAt(0, 0, 0);
  }

  _premiereForme() {
    for (const n of CYCLE_FORMES) if (this._formes[n]) return n;
    return Object.keys(this._formes)[0];
  }

  _construireFormes() {
    this._formes = {};
    const lignes = [];
    for (const nom of this._plan.formes) {
      const f = construireForme(nom, this._plan.cellules);
      const att = ATTENDUS[nom];
      lignes.push(`${nom.padEnd(10)} ${f.sommets.length}/${att.sommets} sommets  `
        + `${f.aretes.length / 2}/${att.aretes} aretes  `
        + `${f.parCellule}/${att.parCellule} par cellule  ${f.tris.length / 3} triangles  `
        + (f.ok ? 'OK' : 'REFUSE'));
      // UNE FORME QUI RATE SON COMPTE N'EST PAS DESSINÉE. Un polytope faux ne
      // se voit pas : c'est un objet qui tourne, joli, et faux. Mieux vaut
      // quatre formes justes que cinq dont une ment.
      if (f.ok) this._formes[nom] = f;
    }
    if (this._verifie && typeof console !== 'undefined')
      // eslint-disable-next-line no-console
      console.log('[polytope]\n' + lignes.join('\n'));
  }

  _creerAretes(maxA, niveau) {
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(
      [0, -1, 0, 1, -1, 0, 1, 1, 0, 0, 1, 0], 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    const att = (n) => {
      const a = new THREE.InstancedBufferAttribute(new Float32Array(maxA * n), n);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };
    g.setAttribute('iA', att(3));
    g.setAttribute('iB', att(3));
    g.setAttribute('iW', att(2));
    g.instanceCount = 0;
    const m = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._uni, { uNiveau: { value: niveau }, uDither: { value: 0 }, uWmax: { value: 0.5 } }),
      vertexShader: ARETE_VERT, fragmentShader: ARETE_FRAG,
      transparent: true, blending: THREE.AdditiveBlending,
      depthTest: false, depthWrite: false, side: THREE.DoubleSide, precision: 'mediump'
    });
    const mesh = new THREE.Mesh(g, m);
    mesh.frustumCulled = false;
    this._scene.add(mesh);
    return mesh;
  }

  _creerSommets(maxS, niveau) {
    const g = new THREE.BufferGeometry();
    const p = new THREE.BufferAttribute(new Float32Array(maxS * 3), 3);
    const w = new THREE.BufferAttribute(new Float32Array(maxS), 1);
    p.setUsage(THREE.DynamicDrawUsage); w.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', p);
    g.setAttribute('aW', w);
    g.setDrawRange(0, 0);
    const m = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._uni, { uNiveau: { value: niveau * SOMMET_NIVEAU }, uWmax: { value: 0.5 } }),
      vertexShader: SOMMET_VERT, fragmentShader: SOMMET_FRAG,
      transparent: true, blending: THREE.AdditiveBlending,
      depthTest: false, depthWrite: false, precision: 'mediump'
    });
    const pts = new THREE.Points(g, m);
    pts.frustumCulled = false;
    this._scene.add(pts);
    return pts;
  }

  _creerCellules(maxT) {
    const g = new THREE.BufferGeometry();
    const p = new THREE.BufferAttribute(new Float32Array(maxT * 3), 3);
    const w = new THREE.BufferAttribute(new Float32Array(maxT), 1);
    p.setUsage(THREE.DynamicDrawUsage); w.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', p);
    g.setAttribute('aW', w);
    g.setDrawRange(0, 0);
    const m = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._uni, { uNiveau: { value: 0 }, uWmax: { value: 0.5 } }),
      vertexShader: CELLULE_VERT, fragmentShader: CELLULE_FRAG,
      transparent: true, blending: THREE.AdditiveBlending,
      depthTest: false, depthWrite: false, side: THREE.DoubleSide, precision: 'mediump'
    });
    const mesh = new THREE.Mesh(g, m);
    mesh.frustumCulled = false;
    this._scene.add(mesh);
    return mesh;
  }

  // LE FOND : les arêtes d'un tesseract géant, immobile, qui englobe la scène.
  // Il ne tourne pas - c'est un repère, et c'est le polytope central qui le
  // contredit. Sans repère fixe, un objet qui se retourne n'a rien à démentir.
  _creerGrille() {
    const f = construireForme('tesseract', false);
    const n = f.aretes.length / 2;
    const mesh = this._creerAretes(n, GRILLE_NIVEAU);
    mesh.material.uniforms.uDither.value = 0.30;
    const iA = mesh.geometry.getAttribute('iA');
    const iB = mesh.geometry.getAttribute('iB');
    const iW = mesh.geometry.getAttribute('iW');
    const E = 5.8;
    for (let e = 0; e < n; e++) {
      const a = f.sommets[f.aretes[e * 2]], b = f.sommets[f.aretes[e * 2 + 1]];
      // Projeté une fois, avec l'oeil au repos : il est IMMOBILE.
      const sa = OEIL4_BASE / (OEIL4_BASE - a[3]), sb = OEIL4_BASE / (OEIL4_BASE - b[3]);
      iA.setXYZ(e, a[0] * sa * E, a[1] * sa * E, a[2] * sa * E);
      iB.setXYZ(e, b[0] * sb * E, b[1] * sb * E, b[2] * sb * E);
      iW.setXY(e, a[3], b[3]);
    }
    iA.needsUpdate = iB.needsUpdate = iW.needsUpdate = true;
    mesh.geometry.instanceCount = n;
    return mesh;
  }

  _appliquerPalette(palette) {
    const vifs = (palette && palette.vividColors && palette.vividColors.length) ? palette.vividColors : null;
    let h = 0.58;
    if (vifs) {
      const c = new THREE.Color(vifs[0]);
      const hsl = { h: 0, s: 0, l: 0 };
      c.getHSL(hsl);
      if (hsl.s > 0.12) h = hsl.h;
    }
    // w négatif prend la teinte de la pochette, w positif sa COMPLÉMENTAIRE.
    // Les deux sont franches : c'est le seul endroit de l'image où la couleur
    // porte une information, et une teinte lavée ne se lirait pas le long
    // d'une arête de deux pixels.
    this._uni.uMoins.value.setHSL(h, 0.85, 0.56);
    this._uni.uPlus.value.setHSL((h + 0.5) % 1, 0.85, 0.56);
  }

  // LA ROTATION 4D. Six plans possibles ; un plan qui contient w est celui qui
  // RETOURNE l'objet, parce qu'un sommet y passe de w positif à w négatif,
  // donc de « proche » à « loin » dans la projection. Un plan sans w (xy, xz,
  // yz) ne fait tourner que la partie 3D et ne retourne rien : c'est la
  // différence que ?polyplane=xw contre ?polyplane=xy doit rendre flagrante.
  _rotation4(src, dst, nS) {
    for (let i = 0; i < nS; i++) {
      const o = i * 4;
      let x = src[i][0], y = src[i][1], z = src[i][2], w = src[i][3];
      for (const nom of ['xy', 'xz', 'xw', 'yz', 'yw', 'zw']) {
        const a = this._vu[nom];
        if (!a) continue;
        const c = Math.cos(a), s = Math.sin(a);
        const p = PLANS[nom];
        const v = [x, y, z, w];
        const u = v[p[0]], t = v[p[1]];
        v[p[0]] = u * c - t * s;
        v[p[1]] = u * s + t * c;
        x = v[0]; y = v[1]; z = v[2]; w = v[3];
      }
      dst[o] = x; dst[o + 1] = y; dst[o + 2] = z; dst[o + 3] = w;
    }
  }

  // LA PROJECTION 4D -> 3D. Un oeil placé en w = uOeil regarde l'hyperplan
  // w = 0 : un sommet dont le w est grand est près de l'oeil et s'éloigne du
  // centre, un sommet dont le w est petit se ramasse au milieu. C'est cette
  // seule formule qui fait passer les cellules intérieures à l'extérieur.
  _projeter(rot, nS, pos3, wArr, oeil, echelle) {
    let wmax = 0;
    for (let i = 0; i < nS; i++) {
      const o = i * 4, w = rot[o + 3];
      if (Math.abs(w) > wmax) wmax = Math.abs(w);
      const k = echelle * oeil / Math.max(0.05, oeil - w);
      pos3[i * 3] = rot[o] * k;
      pos3[i * 3 + 1] = rot[o + 1] * k;
      pos3[i * 3 + 2] = rot[o + 2] * k;
      wArr[i] = w;
    }
    // Un plancher : pendant un instant d'une rotation, tous les w peuvent
    // passer près de zéro, et diviser par là ferait exploser la teinte.
    return Math.max(0.15, wmax);
  }

  _poserWmax(objet, wmax) { objet.material.uniforms.uWmax.value = wmax; }

  _remplirAretes(mesh, forme, pos3, wArr) {
    const g = mesh.geometry;
    const iA = g.getAttribute('iA'), iB = g.getAttribute('iB'), iW = g.getAttribute('iW');
    const a = forme.aretes, n = a.length / 2;
    const A = iA.array, B = iB.array, W = iW.array;
    for (let e = 0; e < n; e++) {
      const p = a[e * 2] * 3, q = a[e * 2 + 1] * 3;
      A[e * 3] = pos3[p]; A[e * 3 + 1] = pos3[p + 1]; A[e * 3 + 2] = pos3[p + 2];
      B[e * 3] = pos3[q]; B[e * 3 + 1] = pos3[q + 1]; B[e * 3 + 2] = pos3[q + 2];
      W[e * 2] = wArr[a[e * 2]]; W[e * 2 + 1] = wArr[a[e * 2 + 1]];
    }
    iA.needsUpdate = iB.needsUpdate = iW.needsUpdate = true;
    g.instanceCount = n;
  }

  _remplirSommets(pts, nS, pos3, wArr) {
    const g = pts.geometry;
    const p = g.getAttribute('position'), w = g.getAttribute('aW');
    p.array.set(pos3.subarray(0, nS * 3));
    w.array.set(wArr.subarray(0, nS));
    p.needsUpdate = w.needsUpdate = true;
    g.setDrawRange(0, nS);
  }

  _remplirCellules(forme, pos3, wArr) {
    const g = this._cellules.geometry;
    const p = g.getAttribute('position'), w = g.getAttribute('aW');
    const t = forme.tris, n = t.length;
    for (let k = 0; k < n; k++) {
      const v = t[k] * 3;
      p.array[k * 3] = pos3[v]; p.array[k * 3 + 1] = pos3[v + 1]; p.array[k * 3 + 2] = pos3[v + 2];
      w.array[k] = wArr[t[k]];
    }
    p.needsUpdate = w.needsUpdate = true;
    g.setDrawRange(0, n);
  }

  // L'ASSIGNATION DU MORPHING : chaque sommet part vers le point d'arrivée
  // LIBRE le plus proche, pris dans l'ordre. Le brief demandait l'assignation
  // triée de Swarm - les deux nuages triés sur une même clef linéaire, appariés
  // rang par rang - et elle ne tient pas ici. Mesuré sur les quatre transitions
  // du cycle : trajet moyen 1.115 en trié contre 1.359 au hasard, soit 18% de
  // mieux que rien, et l'alignement des trajectoires de deux sommets VOISINS
  // sortait à 0.212 contre 0.223 au hasard - pas mieux que le hasard du tout.
  // La raison est dimensionnelle : une clef linéaire unique ordonne bien un
  // nuage quasi plan, comme les formations de Swarm, et perd le voisinage dans
  // un nuage réparti sur toute une hypersphère. Le plus-proche-libre descend à
  // 0.386, soit 2.9 fois plus court, pour 0 à 1 ms par morphing - et il n'y en
  // a qu'un toutes les quatre phrases.
  //
  // Quand l'arrivée compte MOINS de points que le départ, les places libres
  // s'épuisent et les sommets restants se partagent les points déjà pris :
  // plusieurs convergent alors vers le même, ce qui est exactement ce qu'on
  // veut voir quand six cents sommets se replient sur seize.
  _associer(src, dst) {
    const map = new Int32Array(src.length);
    const pris = new Uint8Array(dst.length);
    let libres = dst.length;
    for (let i = 0; i < src.length; i++) {
      const a = src[i];
      let best = 0, bd = Infinity;
      for (let j = 0; j < dst.length; j++) {
        if (libres > 0 && pris[j]) continue;
        const b = dst[j];
        const dx = a[0] - b[0], dy = a[1] - b[1], dz = a[2] - b[2], dw = a[3] - b[3];
        const d = dx * dx + dy * dy + dz * dz + dw * dw;
        if (d < bd) { bd = d; best = j; }
      }
      map[i] = best;
      if (libres > 0) { pris[best] = 1; libres--; }
    }
    return map;
  }

  _choisirSuivante(prog) {
    const dispo = CYCLE_FORMES.filter((n) => this._formes[n]);
    if (dispo.length < 2) return null;
    // Les formes simples en début et en fin de morceau, les riches au milieu :
    // une dentelle de mille deux cents arêtes sur une intro est une bouillie.
    const bord = Math.min(prog, 1 - prog);
    const cible = bord < 0.18 ? 0 : bord < 0.32 ? 1 : 3;
    const depart = CYCLE_FORMES.indexOf(this._nomCourant);
    for (let k = 1; k <= CYCLE_FORMES.length; k++) {
      const n = CYCLE_FORMES[(depart + k) % CYCLE_FORMES.length];
      if (this._formes[n] && n !== this._nomCourant && RICHESSE[n] <= cible) return n;
    }
    for (let k = 1; k <= CYCLE_FORMES.length; k++) {
      const n = CYCLE_FORMES[(depart + k) % CYCLE_FORMES.length];
      if (this._formes[n] && n !== this._nomCourant) return n;
    }
    return null;
  }

  update(deltaTime, profile) {
    // PAUSE : gel. La rotation reste figée ENTRE deux crans, ce qui est la
    // seule position où un objet qui avance par crans a l'air arrêté plutôt
    // que cassé.
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._temps += dt;
    const u = this._uni;
    const director = profile.director;
    const bandes = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const low = bandes ? clamp01(bandes.low) : 0;
    const mid = bandes ? clamp01(bandes.mid) : 0;
    const high = bandes ? clamp01(bandes.high) : 0;

    if (typeof window !== 'undefined') u.uDpr.value = Math.min(window.devicePixelRatio || 1, 2);
    if (profile.artworkPalette !== this._paletteVue) {
      this._paletteVue = profile.artworkPalette;
      this._appliquerPalette(profile.artworkPalette);
    }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondesBeat = bpm > 0 ? 60 / bpm : 0.5;

    // --- LES FRONTS. Un temps, une mesure, une phrase.
    let battement = false;
    const bp = profile.beatPhase;
    if (bp != null) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) battement = true;
      this._phaseBeat = bp;
    } else {
      this._libre = (this._libre || 0) + dt;
      if (this._libre >= this._secondesBeat) { this._libre -= this._secondesBeat; battement = true; }
    }
    if (this._pasLibre) {
      battement = this._temps - this._pasLibreAt > 1.5;
      if (battement) this._pasLibreAt = this._temps;
    }
    if (battement) this._battements++;

    let mesure = false;
    const bar = profile.barPhase;
    if (bar != null) {
      if (this._phaseBar != null && bar < this._phaseBar - 0.5) mesure = true;
      this._phaseBar = bar;
    } else if (battement && this._battements % 4 === 0) mesure = true;

    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) {
      if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true;
      this._phasePhrase = ph;
    } else if (battement && this._battements % TEMPS_PAR_PHRASE === 0) phrase = true;

    // --- LE CRAN. Un seizième de tour par temps sur le plan principal ; seize
    // temps font une phrase, donc une révolution complète d'angle accumulé par
    // phrase, exactement comme demandé - le plan sur lequel elle se répartit
    // change de mesure en mesure, mais le total ne bouge pas.
    if (battement) this._cible[this._planPrincipal] += (Math.PI * 2) / TEMPS_PAR_PHRASE;
    if (mesure) {
      this._mesures++;
      if (!this._planForce) this._planPrincipal = CYCLE_PLANS[this._mesures % CYCLE_PLANS.length];
      this._pulseSommets = 1;
    }
    this._pulseSommets = Math.max(0, (this._pulseSommets || 0) - dt * 3.0);

    // L'interpolation du cran : une constante de temps telle que l'écart est
    // résorbé en CRAN_MS. Exponentielle et non linéaire, pour que le pas soit
    // indépendant de la cadence d'images.
    const k = 1 - Math.exp(-dt / (CRAN_MS / 3000));
    // Tous les plans à crans, y compris un plan forcé qui ne serait pas dans
    // le cycle : sinon ?polyplane=xy fige l'objet au lieu de le faire tourner.
    for (const nom of (this._planForce ? [this._planForce] : CYCLE_PLANS))
      this._vu[nom] += (this._cible[nom] - this._vu[nom]) * k;

    // --- LES ROTATIONS CONTINUES. bands.mid pousse la secondaire.
    // ?polyplane les COUPE. Le drapeau doit isoler un plan et un seul : la
    // tertiaire tourne dans zw, qui contient w, et tant qu'elle tournait
    // ?polyplane=xy retournait quand même le polytope - la démonstration
    // demandée par le critère 2 ne démontrait rien.
    if (!this._planForce) {
      const vitMid = 0.70 + 0.60 * mid;
      const tour = Math.PI * 2;
      this._cible.yz += dt * (tour / (2 * TEMPS_PAR_PHRASE * this._secondesBeat)) * vitMid;
      this._vu.yz = this._cible.yz;
      const dTer = dt * (tour / (4 * TEMPS_PAR_PHRASE * this._secondesBeat)) * vitMid;
      this._cible.zw += dTer; this._vu.zw += dTer;
    }

    // --- L'OEIL 4D. Sur un drop il s'effondre le temps d'un temps, puis
    // remonte en deux mesures. Il ne descend jamais sous OEIL4_MIN : à w = 1
    // l'échelle passerait par l'infini.
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._modePrec !== 'drop') {
      this._dropTenue = this._temps + this._secondesBeat;
      this._dropJusqu = this._dropTenue + DROP_MESURES * 4 * this._secondesBeat;
    }
    this._modePrec = mode;
    let oeil;
    if (this._temps < this._dropTenue) oeil = OEIL4_DROP;
    else if (this._temps < this._dropJusqu) {
      const t = (this._temps - this._dropTenue) / Math.max(1e-3, this._dropJusqu - this._dropTenue);
      oeil = OEIL4_DROP + (OEIL4_BASE - OEIL4_DROP) * (t * t * (3 - 2 * t));
    } else oeil = OEIL4_BASE;
    // Les graves rapprochent l'oeil 4D : la projection respire avec le kick.
    oeil *= 1 - OEIL4_BANDE * low;
    if (this._oeilForce != null) oeil = this._oeilForce;
    this._oeil = Math.max(OEIL4_MIN, oeil);

    // --- LA SÉQUENCE. Une phrase majeure sur quatre déclenche le morphing.
    if (phrase) {
      this._phrases++;
      if (this._morph < 0 && !this._formeForcee && this._phrases % 4 === 0) {
        const suivant = this._choisirSuivante(profile.duration > 0 ? clamp01(profile.progress) : 0.5);
        if (suivant) {
          this._nomSuivant = suivant;
          this._assoc = this._associer(this._formes[this._nomCourant].sommets,
                                       this._formes[suivant].sommets);
          this._morph = 0;
        }
      }
    }

    // --- LA GÉOMÉTRIE.
    const fA = this._formes[this._nomCourant];
    const nA = fA.sommets.length;
    this._rotation4(fA.sommets, this._rot, nA);

    if (this._morph >= 0) {
      const fB = this._formes[this._nomSuivant];
      const nB = fB.sommets.length;
      this._rotation4(fB.sommets, this._rotB, nB);
      this._morph = Math.min(1, this._morph + dt / MORPH_S);
      const t = this._morph, e = t * t * (3 - 2 * t);
      // Les sommets de DÉPART volent vers leur cible ; l'interpolation se fait
      // en 4D, donc l'objet intermédiaire continue de tourner et de se
      // retourner pendant le vol.
      for (let i = 0; i < nA; i++) {
        const o = i * 4, q = this._assoc[i] * 4;
        for (let c = 0; c < 4; c++)
          this._rot[o + c] += (this._rotB[q + c] - this._rot[o + c]) * e;
      }
      // L'échelle s'interpole AUSSI : sans cela l'objet change de taille d'un
      // coup au moment où les arêtes se reconnectent.
      const ech = fA.echelle + (fB.echelle - fA.echelle) * e;
      const wmA = this._projeter(this._rot, nA, this._pos3, this._w, this._oeil, ech);
      const wmB = this._projeter(this._rotB, nB, this._pos3B, this._wB, this._oeil, fB.echelle);
      this._poserWmax(this._aretesA, wmA); this._poserWmax(this._sommetsA, wmA);
      this._poserWmax(this._aretesB, wmB); this._poserWmax(this._sommetsB, wmB);
      this._remplirAretes(this._aretesA, fA, this._pos3, this._w);
      this._remplirAretes(this._aretesB, fB, this._pos3B, this._wB);
      this._remplirSommets(this._sommetsA, nA, this._pos3, this._w);
      this._remplirSommets(this._sommetsB, nB, this._pos3B, this._wB);
      // LE FONDU CROISÉ sur le dernier tiers, et les deux niveaux SOMMENT À 1 :
      // à l'atterrissage les deux jeux d'arêtes se superposent exactement, et
      // en additif un simple fondu entrant doublerait la clarté de l'objet.
      const f = clamp01((t - 0.66) / 0.34);
      const gA = GAIN_FORME[fA.nom] || 1, gB = GAIN_FORME[fB.nom] || 1;
      this._aretesA.material.uniforms.uNiveau.value = (1 - f) * gA;
      this._aretesB.material.uniforms.uNiveau.value = f * gB;
      this._sommetsA.material.uniforms.uNiveau.value = (1 - f) * SOMMET_NIVEAU * gA;
      this._sommetsB.material.uniforms.uNiveau.value = f * SOMMET_NIVEAU * gB;
      // Les cellules s'effacent pendant le vol : des faces posées sur des
      // sommets interpolés ne sont les faces de rien.
      if (this._cellules) this._cellules.material.uniforms.uNiveau.value = 0;
      if (t >= 1) {
        this._nomCourant = this._nomSuivant; this._nomSuivant = null;
        this._morph = -1; this._assoc = null;
        this._aretesB.geometry.instanceCount = 0;
        this._sommetsB.geometry.setDrawRange(0, 0);
        const g = GAIN_FORME[this._nomCourant] || 1;
        this._aretesA.material.uniforms.uNiveau.value = g;
        this._sommetsA.material.uniforms.uNiveau.value = SOMMET_NIVEAU * g;
      }
    } else {
      const g = GAIN_FORME[fA.nom] || 1;
      this._aretesA.material.uniforms.uNiveau.value = g;
      this._sommetsA.material.uniforms.uNiveau.value = SOMMET_NIVEAU * g;
      const wm = this._projeter(this._rot, nA, this._pos3, this._w, this._oeil, fA.echelle);
      this._poserWmax(this._aretesA, wm); this._poserWmax(this._sommetsA, wm);
      if (this._cellules) this._poserWmax(this._cellules, wm);
      this._remplirAretes(this._aretesA, fA, this._pos3, this._w);
      this._remplirSommets(this._sommetsA, nA, this._pos3, this._w);
      if (this._cellules) {
        if (fA.tris.length) {
          this._remplirCellules(fA, this._pos3, this._w);
          this._cellules.material.uniforms.uNiveau.value = CELLULE_NIVEAU;
        } else this._cellules.material.uniforms.uNiveau.value = 0;
      }
    }

    // --- LE SCINTILLEMENT DES SOMMETS, sur bands.high, plus la pulsation de
    // mesure.
    u.uScint.value = clamp01(high * 0.8 + this._pulseSommets * 0.9);

    // --- LA CAMÉRA. Une orbite lente : la rotation 4D est déjà le mouvement
    // principal, la caméra ne fait que révéler le volume 3D de la projection.
    const a = (this._temps / CAMERA_TOUR_S) * Math.PI * 2;
    const incl = 1.15 + 0.85 * Math.sin(this._temps * 0.043);
    this._camera.position.set(Math.cos(a) * CAMERA_DIST, incl, Math.sin(a) * CAMERA_DIST);
    this._camera.lookAt(0, 0, 0);
  }

  dispose() {
    for (const o of [this._aretesA, this._aretesB, this._sommetsA, this._sommetsB,
                     this._cellules, this._grille]) {
      if (!o) continue;
      o.onBeforeRender = null;
      this._scene.remove(o);
      o.geometry.dispose();
      o.material.dispose();
    }
    this._aretesA = this._aretesB = this._sommetsA = this._sommetsB = null;
    this._cellules = this._grille = null;
    this._formes = null;
  }
}

export default PolytopeVisualizer;
